/**
 * The set of actions that can occur when visiting a registry url.
 *
 * In most cases, the new org-registries and the team-level Model Registry
 * use different URLs. But there is one path which is common between the two,
 * i.e. "/registry/:registryName". Furthermore, the page that this url loads
 * depends on whether the org-registry feature flag is enabled for the viewer's
 * organization or not. This complicates the routing logic for registries.
 *
 * To remove the common urls between the two products, we have also added a new url
 * for the Model registry, that allows us to view the registered models of all teams'
 * model registries in one page. Without the org-registry feature flag, this page used
 * to be loaded at "/registry/model", but now it will be loaded at "/teams/registry/model".
 *
 * For more information on this, visit:
 * https://www.notion.so/wandbai/Design-doc-Org-scoped-registry-URLS-implementation-10de2f5c7ef380edb11bd6786170404e
 */

import {
  ENTITY_REGISTRY_ACCESS_CONTROL_PATH,
  OLD_ENTITY_REGISTRY_ALL_TEAMS_PATHS,
  OLD_ORG_REGISTRY_SETTINGS_PATH,
  ORG_REGISTRY_PATHS,
  ORG_SCOPED_REGISTRY_SETTINGS_PATH,
} from '../../routes/paths';
import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../util/artifacts';
import * as urls from '../../util/urls';
import {checkRegistryProject, isOrgRegistryProjectName} from './common/utils';

export type RegistryAction =
  | {
      type: 'redirect';
      to: string;
    }
  | {
      type: 'load';
      componentName: 'PanelArtifactDetailView';
      props: {registryName: string};
    }
  | {
      type: 'load';
      componentName: 'RegistriesHomepage';
      props: {registryName?: string; loadSettingsPage: boolean};
    }
  | {
      type: 'load';
      componentName: 'EntityRegistryAccessControlsPage';
      props: {entityName: string};
    }
  | {type: 'load'; componentName: 'ModelRegistryPage'}
  | {
      type: 'load';
      componentName: 'NoAccessPage';
      props: {contentKey: 'noModelsAccess'};
    };

// Helper for handling no access logic
const handleNoAccess = (hasModelsAccess: boolean): RegistryAction | null => {
  if (!hasModelsAccess) {
    return {
      type: 'load',
      componentName: 'NoAccessPage',
      props: {contentKey: 'noModelsAccess'},
    };
  }
  return null;
};

const determineOrgRegistryAction = (
  hasModelsAccess: boolean,
  orgName: string | undefined,
  accountOrgName: string,
  entityName: string | undefined,
  registryName: string | undefined,
  path: string,
  search: string
): RegistryAction => {
  const {
    selectionPath,
    selectedCollectionView,
    selectedMembershipIdentifier,
    selectedMembershipTab,
  } = urls.parseEntityRegistrySearch(search);

  const isInvalidOrgName = orgName != null && accountOrgName !== orgName;
  if (isInvalidOrgName || OLD_ENTITY_REGISTRY_ALL_TEAMS_PATHS.includes(path)) {
    return {
      type: 'redirect',
      to: urls.globalRegistry(
        accountOrgName,
        registryName,
        selectionPath,
        selectedCollectionView,
        selectedMembershipIdentifier,
        selectedMembershipTab,
        OLD_ORG_REGISTRY_SETTINGS_PATH === path
      ),
    };
  }

  // This check is relevant in the case where the URL prefix is that of the
  // entity-scoped team model registry, but the selected collection is from an
  // org-registry (we identify this using the collection's project name.
  if (entityName != null) {
    return {
      type: 'redirect',
      to: urls.globalRegistry(
        orgName ?? accountOrgName,
        registryName,
        selectionPath,
        selectedCollectionView,
        selectedMembershipIdentifier,
        selectedMembershipTab
      ),
    };
  }

  // Route directly to the internal collection component if search params are
  // provided in the url.
  if (registryName != null && search !== '') {
    const {registryName: registryNameFromSearch} = checkRegistryProject(
      selectionPath?.projectName
    );
    if (registryName !== registryNameFromSearch) {
      return {
        type: 'redirect',
        to: urls.globalRegistry(
          orgName ?? accountOrgName,
          registryNameFromSearch,
          selectionPath,
          selectedCollectionView,
          selectedMembershipIdentifier,
          selectedMembershipTab
        ),
      };
    }
    return (
      handleNoAccess(hasModelsAccess) || {
        type: 'load',
        componentName: 'PanelArtifactDetailView',
        props: {registryName},
      }
    );
  }
  return (
    handleNoAccess(hasModelsAccess) || {
      type: 'load',
      componentName: 'RegistriesHomepage',
      props: {
        loadSettingsPage: ORG_SCOPED_REGISTRY_SETTINGS_PATH === path,
        registryName,
      },
    }
  );
};

export const determineRegistryAction = (
  enableGlobalRegistry: boolean,
  hasModelsAccess: boolean,
  orgName: string | undefined,
  accountOrgName: string,
  entityName: string | undefined,
  registryName: string | undefined,
  path: string,
  search: string,
  viewerTeams: string[],
  viewerIsNull: boolean,
  isPersonalEntity: boolean
): RegistryAction => {
  const {
    selectionPath,
    selectedCollectionView,
    selectedMembershipIdentifier,
    selectedMembershipTab,
  } = urls.parseEntityRegistrySearch(search);
  if (enableGlobalRegistry) {
    // use org-registry action
    const isOrgRegistryPath =
      ORG_REGISTRY_PATHS.includes(path) ||
      isOrgRegistryProjectName(selectionPath?.projectName ?? '');
    const isLegacyModelRegistryProject =
      selectionPath?.projectName === OLD_MODEL_REGISTRY_PROJECT_NAME;
    if (isOrgRegistryPath && !isLegacyModelRegistryProject) {
      return determineOrgRegistryAction(
        hasModelsAccess || viewerIsNull,
        orgName,
        accountOrgName,
        entityName,
        registryName,
        path,
        search
      );
    }
  }

  // This case is highly unlikely as users for whom the feature flag is disabled
  // will likely not know of this url structure. But just in case they do, we
  // will redirect them to their model registry homepage if they try to access it.
  // This will also apply to any anon users trying to access the page
  // As of now, the new W&B Registry doesn't have a public view (anon-empty-state).
  if (!enableGlobalRegistry && ORG_REGISTRY_PATHS.includes(path)) {
    return {
      type: 'redirect',
      to: urls.objectRegistry('model'),
    };
  }
  // 1. If the global registry feature flag is disabled,
  // redirect users from /registry/model to /teams/registry/model
  // 2. Validate and correct registry name for team-level registry
  const isOldAllTeamsPath =
    entityName == null && OLD_ENTITY_REGISTRY_ALL_TEAMS_PATHS.includes(path);
  const entityNameDoesNotMatchSearchCollectionPath =
    search !== '' && selectionPath?.entityName !== entityName;
  if (
    registryName !== 'model' ||
    isOldAllTeamsPath ||
    entityNameDoesNotMatchSearchCollectionPath
  ) {
    if (registryName != null && registryName !== 'model') {
      console.warn('Registry name has to be of type model');
    }
    return {
      type: 'redirect',
      to: urls.objectRegistry(
        'model',
        selectionPath,
        selectionPath?.entityName ?? entityName,
        selectedCollectionView,
        selectedMembershipIdentifier,
        selectedMembershipTab
      ),
    };
  }

  // Route directly to the internal collection component if search params are
  // provided in the url
  if (registryName != null && search !== '') {
    return (
      handleNoAccess(hasModelsAccess || viewerIsNull) || {
        type: 'load',
        componentName: 'PanelArtifactDetailView',
        props: {registryName},
      }
    );
  }

  // If the url entity is not a valid team that the viewer is a member of then
  // redirect to the all-teams view
  const isValidTeam =
    entityName == null ||
    viewerTeams.some(teamName => teamName === entityName) ||
    isPersonalEntity;
  if (!isValidTeam && !viewerIsNull) {
    return {
      type: 'redirect',
      to: urls.objectRegistry('model'),
    };
  }

  if (ENTITY_REGISTRY_ACCESS_CONTROL_PATH === path && entityName != null) {
    if (viewerIsNull) {
      return {
        type: 'redirect',
        to: urls.objectRegistry(registryName, undefined, entityName),
      };
    }
    return (
      handleNoAccess(hasModelsAccess || viewerIsNull) || {
        type: 'load',
        componentName: 'EntityRegistryAccessControlsPage',
        props: {entityName},
      }
    );
  }
  return (
    handleNoAccess(hasModelsAccess || viewerIsNull) || {
      type: 'load',
      componentName: 'ModelRegistryPage',
    }
  );
};
